<script setup lang="ts">
/** Project menu (role="menu", arrow keys, Escape): save, restore, history, presets, new project, theme. */
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { createDesign, fieldsForManualSetup } from '@/domain';
import Icon from './Icon.vue';
import { app, ui, type Theme } from '@/app/state';
import { announce, toast } from '@/app/announce';
import { choose } from '@/app/analysis';
import { go } from '@/app/navigation';
import { newProject, readBrowserCopy, restoreSaved, saveNow, setTheme } from '@/app/persistence';
import { fmtDate, fmtTime } from '@/app/format';
import { ApiError } from '@/app/api';

const wrap = ref<HTMLElement | null>(null);
const btn = ref<HTMLButtonElement | null>(null);
const menu = ref<HTMLElement | null>(null);
const saved = ref<ReturnType<typeof readBrowserCopy>>({ ok: false, reason: null });
const savedLabel = computed(() => (saved.value.ok ? fmtDate(saved.value.snapshot.savedAt) : ''));

function items(): HTMLElement[] { return [...(menu.value?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? [])]; }
async function toggle(open = !ui.menuOpen) {
  if (open) {
    saved.value = readBrowserCopy();
    ui.menuOpen = true;
    await nextTick();
    items()[0]?.focus();
  } else ui.menuOpen = false;
}
function close({ focus = false } = {}) { ui.menuOpen = false; if (focus) btn.value?.focus(); }
function onKey(e: KeyboardEvent) {
  const list = items();
  const i = list.indexOf(document.activeElement as HTMLElement);
  if (e.key === 'ArrowDown') { e.preventDefault(); list[(i + 1) % list.length]?.focus(); }
  if (e.key === 'ArrowUp') { e.preventDefault(); list[(i - 1 + list.length) % list.length]?.focus(); }
  if (e.key === 'Home') { e.preventDefault(); list[0]?.focus(); }
  if (e.key === 'End') { e.preventDefault(); list.at(-1)?.focus(); }
  if (e.key === 'Escape') { e.preventDefault(); close({ focus: true }); }
  if (e.key === 'Tab') close();
}
function onDocClick(e: MouseEvent) { if (ui.menuOpen && !wrap.value?.contains(e.target as Node)) close(); }
onMounted(() => document.addEventListener('click', onDocClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocClick));

function act(name: string) {
  if (name === 'restore-saved' && !saved.value.ok) return;
  close({ focus: true });
  if (name === 'save') void saveNow();
  if (name === 'restore-saved') restoreSaved();
  if (name === 'history') ui.historyOpen = true;
  if (name === 'even' || name === 'residual') {
    if (ui.empty) {
      const d = createDesign(name, app.store.stock);
      app.store.reset({ design: d, fields: fieldsForManualSetup(d, app.store.stock), revisions: [], currentRevisionId: null });
      ui.analysis = { status: 'manual', ranAt: new Date().toISOString() };
      ui.empty = false;
      go('check');
    } else {
      app.store.apply(() => ({ ...createDesign(name, app.store.stock), appearance: { ...app.store.design.appearance } }), { label: name === 'even' ? 'Even preset' : 'Residual preset' });
    }
    toast(name === 'even' ? 'Loaded the even preset: 800 × 1200 × 300, 3 shelves. Undo to go back.' : 'Loaded the residual preset: 800 × 1200 × 300, 6 shelves (openings 150.9 and 150.8 mm). Undo to go back.');
  }
  if (name === 'new') confirmNew();
}
function confirmNew() {
  const name = app.store.design.appearance?.name || 'this project';
  choose({
    title: 'Start a new project?',
    lede: `You will start from an empty reference. ${name} stays saved in its project file, and you can open it again later.`,
    choices: [
      { label: 'Start new project', primary: true, act: () => { void newProject({ from: 'blank' }).then(() => { go('reference'); announce('New project started.'); }).catch((e) => toast(e instanceof ApiError ? e.message : 'A new project could not be created.', { tone: 'warn' })); } },
      { label: `Keep working on ${name}`, act: () => {} },
    ],
  });
}
function theme(t: Theme) { setTheme(t); }
</script>

<template>
  <div ref="wrap" class="menu-wrap">
    <button id="menuBtn" ref="btn" type="button" class="icon-btn" aria-haspopup="menu" :aria-expanded="ui.menuOpen ? 'true' : 'false'" aria-controls="menu" aria-label="Project menu" @click.stop="toggle()"><Icon name="menu" /></button>
    <div v-if="ui.menuOpen" id="menu" ref="menu" class="menu" role="menu" aria-label="Project menu" @keydown="onKey">
      <a role="menuitem" href="/?construction">Photo model preview</a>
      <div class="menu-group" role="group" aria-label="Project">
        <button type="button" role="menuitem" data-act="save" @click="act('save')"><Icon name="save" /><span>Save now<small v-if="ui.savedAt">Last saved {{ fmtTime(ui.savedAt) }} · project file and this device</small></span><kbd>⌘S</kbd></button>
        <button type="button" role="menuitem" data-act="restore-saved" :aria-disabled="saved.ok ? undefined : 'true'" @click="act('restore-saved')">
          <Icon name="history" /><span v-if="saved.ok">Restore the copy on this device <small>{{ savedLabel }}</small></span><span v-else>Nothing saved on this device yet</span>
        </button>
        <button type="button" role="menuitem" data-act="history" @click="act('history')"><Icon name="history" /><span>Revision history</span></button>
        <button type="button" role="menuitem" data-act="even" @click="act('even')"><Icon name="ruler" /><span>Start from the even preset <small>800 × 1200 × 300</small></span></button>
        <button type="button" role="menuitem" data-act="residual" @click="act('residual')"><Icon name="ruler" /><span>Start from the residual preset <small>800 × 1200 × 300, 6 shelves</small></span></button>
        <button type="button" role="menuitem" data-act="new" @click="act('new')"><Icon name="plus" /><span>New project</span></button>
      </div>
      <div class="menu-group" role="group" aria-label="Theme">
        <span class="menu-label" aria-hidden="true">Theme</span>
        <div class="menu-seg">
          <button v-for="t in (['system', 'light', 'dark'] as const)" :key="t" type="button" role="menuitemradio" :aria-checked="ui.theme === t ? 'true' : 'false'" :data-theme-set="t" @click="theme(t)">{{ t === 'system' ? 'System' : t === 'light' ? 'Light' : 'Dark' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>
